import React from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import * as toTitleCase from 'title-case';
import { SyncOutlined } from '@ant-design/icons';
// Local Deps
import { Modal, message, Tag } from 'antd';

import EditUser from '../../Modals/EditUser';

import unblockUser from './graphql';
import './ViewUsersGrid.scss';
import Actions from './subComponents/actions';

import msgs from '../../../config/messages';
import {
  requestPassword,
  deleteUser,
  UserForApproveUser,
} from '../../../graphql/users';
import {
  createUser,
} from '../../../graphql/updateUsers';
import { getOrgAdmins } from '../../../graphql/organizationDetails';

const confirm = Modal.confirm;

export default class ViewUsersGrid extends React.PureComponent {
  static propTypes = {
    users: PropTypes.array,
    refreshUsers: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    organizationsStatus: PropTypes.string.isRequired,
    organizationsName: PropTypes.string.isRequired,
    organizationsCage: PropTypes.string.isRequired,
    auth: PropTypes.shape({
      jwtToken: PropTypes.string.isRequired,
      expiration: PropTypes.number.isRequired,
      organizationId: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
    }).isRequired,
  };

  static defaultProps = {
    users: [],
  };
  static initialState = {
    noUsers: false,
    selectedUserId: undefined,
    showEditModal: false,
  };

  state = ViewUsersGrid.initialState;

  formatUsers(users) {
    const { organizationsName, organizationsCage } = this.props;
    if (users[0] !== null) {
      return users.map(user => ({
        ...user,
        name: toTitleCase(user.name),
        role: toTitleCase(user.role),
        phone: user.phoneNumber || user.phone || '',
        iconStatus: toTitleCase(user.iconStatus),
        organizationsName: user.orgName || organizationsName || '',
        organizationsCage: user.cageCode || organizationsCage || '',
      }));
    }
    return [];
  }

  // By default, it will be the organization ID of the user logged in. If deleting from another organization, the proper ID is passed in.
  _deleteUser = async (
    id,
    name,
    organizationId = this.props.auth.organizationId,
  ) => {
    const self = this
    const {
      organization: { admins },
    } = await getOrgAdmins(organizationId);
    if (admins.length === 1 && admins[0].id === id) {
      // we need to check if there is more than 1 admin, and if the user being deleted is that admin
      confirm({
        title: 'You cannot do that',
        content: 'Last admin cannot be deleted',
      });
    } else {
      confirm({
        title: `Are you sure?`,
        content: `You are about to delete ${name} from your organization. 
        This action cannot be undone`,
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'No',
        async onOk() {
          await deleteUser(id);
          self.props.refreshUsers();
        },
      });
    }
  };

  requestUserResetPassword = async id => {
    const res = await requestPassword(id);
    if (res.requestPasswordReset && res.requestPasswordReset.id) {
      message.success('successfully sent');
    } else {
      message.error('failed to send email');
    }
  };

  unblockUser = async id => {
    try {
      await unblockUser(id);
      message.success('Successfully Unblocked User');
    } catch (error) {
      message.error('Something went wrong! Please try again');
    }
    this.props.refreshUsers();
  };

  approveUser = async original => {
    let response = await UserForApproveUser(original.id);
    const user = response.user;
    switch (user.role.toUpperCase()) {
      case 'COMMON_USER':
      case 'GOVERNMENT_USER':
        user.role = 'user';
        break;
      case 'COMMON_ADMIN':
        user.role = 'admin';

        break;
      case 'SUPER_ADMIN':
        user.role = 'super';

        break;

      default:
        user.role = 'user';

        break;
    }
    user.iconStatus = 'approved'
    console.log("user for apporoval", user)
    response = await createUser(user);
    await this.props.refreshUsers();
  };

  closeEditModal = () => {
    this.setState({
      showEditModal: false,
      selectedUserId: undefined,
    });
  }

  openEditModal = (itemId) => {
    this.setState({
      showEditModal: true,
      selectedUserId: itemId,
    });
  }

  render() {
    const { users, loading, organizationsStatus } = this.props;

    const columns = [
      {
        Header: 'Full Name',
        accessor: 'name',
      },

      {
        Header: 'Email',
        accessor: 'email',
      },
      {
        Header: 'Phone Number',
        accessor: 'phone',
      },
      {
        Header: 'Organization Name',
        accessor: 'organizationsName',
      },
      {
        Header: 'CAGE',
        accessor: 'organizationsCage',
      },
      {
        Header: 'Account Status',
        accessor: 'iconStatus',
      },
      {
        Header: 'Role',
        accessor: 'role',
      },
      {
        Header: 'Action',
        accessor: 'action',
        minWidth: 130,
        Cell: cellProps => {
          if (
            organizationsStatus === 'approved' ||
            cellProps.original.orgStatus === 'approved'
          ) {
            return (
              <Actions
                unblockUser={this.unblockUser}
                cellProps={cellProps}
                deleteUser={this._deleteUser}
                approveUser={this.approveUser}
                requestUserResetPassword={this.requestUserResetPassword}
                openEditModal={this.openEditModal}
              // requestOrganizationById={this.requestOrganizationById}
              />
            );
          }
          return <Tag>Organization must be approved</Tag>;
        },
      },
    ];

    const pageSize = 19;
    return (
      <div className="nf-view-users-grid">
        <div style={{ display: 'none' }}>
          {!loading && (
            <SyncOutlined
              onClick={() => {
                this.props.refreshUsers();
              }} />
          )}
          {loading && (
            <SyncOutlined
              spin
              onClick={() => {
                this.props.refreshUsers();
              }} />
          )}
        </div>
        <ReactTable
          className="-striped -highlight"
          data={this.formatUsers(users)}
          columns={columns}
          showPageSizeOptions={false}
          showPagination={users.length > pageSize}
          noDataText={msgs.ViewUsersGrid.noDataText}
          defaultPageSize={pageSize}
          SubComponent={false}
        />
        {this.state.showEditModal && this.state.selectedUserId &&
          <EditUser
            selectedUserId={this.state.selectedUserId}
            showEditModal={this.state.showEditModal}
            auth={this.props.auth}
            closeEditModal={this.closeEditModal}
            refreshUsers={this.props.refreshUsers}
          />
        }
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/grids/ViewUsersGrid/ViewUsersGrid.jsx